import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { combineLatest, map } from 'rxjs';
import { PublicFormsService } from '@core/services/public-forms/public-forms.service';

import { ButtonModule } from 'primeng/button';
import { FloatLabel } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';

type FormStatus = 'idle' | 'loading' | 'success' | 'error';

@Component({
  selector: 'd2d-contact-form',
  imports: [
    ReactiveFormsModule,
    InputTextModule,
    ButtonModule,
    FloatLabel,
    TextareaModule,
    SelectModule,
    TranslocoPipe,
  ],
  templateUrl: './contact-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './contact-form.component.scss',
})
export class ContactFormComponent {
  private readonly formsService = inject(PublicFormsService);
  private readonly translocoService = inject(TranslocoService);

  readonly formStatus = signal<FormStatus>('idle');

  contactForm = new FormGroup({
    type: new FormControl<{ title: string; id: number } | null>(null),
    name: new FormControl('', [Validators.required, Validators.minLength(2)]),
    email: new FormControl('', [Validators.required, Validators.email]),
    message: new FormControl('', [Validators.required, Validators.minLength(5)]),
  });

  private static readonly OPTION_KEYS: { key: string; id: number }[] = [
    { key: 'contactForm.options.workWithUs', id: 1 },
    { key: 'contactForm.options.technicalSupport', id: 2 },
    { key: 'contactForm.options.shipmentIssues', id: 3 },
    { key: 'contactForm.options.suggestions', id: 4 },
  ];

  optionsContact = toSignal(
    combineLatest(
      ContactFormComponent.OPTION_KEYS.map(({ key, id }) =>
        this.translocoService.selectTranslate(key).pipe(map((title) => ({ title, id }))),
      ),
    ),
    { initialValue: [] },
  );

  get isLoading(): boolean {
    return this.formStatus() === 'loading';
  }

  get isSuccess(): boolean {
    return this.formStatus() === 'success';
  }

  get isError(): boolean {
    return this.formStatus() === 'error';
  }

  onSubmit(): void {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    this.formStatus.set('loading');

    const { name, email, message, type } = this.contactForm.value;

    this.formsService
      .submitInquiry({
        name: name!,
        email: email!,
        message: message!,
        message_type: type?.title,
      })
      .subscribe({
        next: () => {
          this.formStatus.set('success');
          this.contactForm.reset();
        },
        error: () => {
          this.formStatus.set('error');
        },
      });
  }
}
